import { useEffect, useMemo, useRef } from 'react';
import * as Cesium from 'cesium';
import { useQuery } from '@tanstack/react-query';
import { useAssetOverview } from '../context/AssetOverviewContext';
import { useInfrastructureMapLayers } from './useInfrastructureMap';
import AssetManagerService from '../services/AssetManager/assetManager.service';
import MapPortalService from '../services/MapPortal/mapportal.service';
import { drawAssetHighlight, flyToAsset } from '../views/CommonMap/assetHighlight.utils';
import { toZoomBounds } from '../views/CommonMap/viewport.utils';
import { NO_FEATURES_TO_ZOOM_MESSAGE, ZOOM_TO_LAYER_SECONDS } from '../views/CommonMap/constants';
import { NotificationTypes } from '../enums/notificationTypes';
import Notification from '../shared/components/Notification';
import { resolveFeatureLook } from '../views/MapPortal/LayerProperties/Symbology/symbologyConfig.utils';
import { parseHighlightColor, resolveHighlightStyle } from '../shared/utils/toolHighlightSld';
import { ASSET_HIGHLIGHT_COLOR } from '../views/InfrastructureManager/AssetOverview/constants';
import type { AssetKey } from '../types/InfrastructureManagerTypes';
import type { MapEngine } from '../types/CommonMapTypes';

const HIGHLIGHT_KEY = 'asset-overview::selection';
const NO_ASSET: AssetKey = { layerId: 0, keyValue: '' };
const HIGHLIGHT_HEX = parseHighlightColor(ASSET_HIGHLIGHT_COLOR).fill;
const STATISTIC_UNDER_COLORS = [HIGHLIGHT_HEX];
const NO_UNDER_COLORS: string[] = [];

// Shares the expanded row's details query, so selecting an expanded asset costs no extra fetch.
export const useAssetMapHighlight = (engine: MapEngine | null) => {
  const { selected, activeStatistic } = useAssetOverview();
  const { getAssetDetails } = AssetManagerService();
  const { getLayerBoundsQuery } = MapPortalService();
  const layersById = useInfrastructureMapLayers();
  const statisticId = activeStatistic?.id ?? null;
  const statisticLayerName = activeStatistic ? layersById.get(activeStatistic.layerId)?.uniqueLayerName : undefined;
  // A statistic filters one layer, so its extent is a stable target; layer rows carry no bounds, so they come from the API.
  const { data: statisticBounds, isSuccess: isStatisticBoundsLoaded } = useQuery({
    ...getLayerBoundsQuery(statisticLayerName ?? ''),
    enabled: !!statisticLayerName,
  });
  const statisticExtent = useMemo(() => toZoomBounds(statisticBounds), [statisticBounds]);
  const isOverStatistic = !!selected && selected.layerId === activeStatistic?.layerId;
  const { data: details } = useQuery({ ...getAssetDetails(selected ?? NO_ASSET), enabled: !!selected });
  const isCurrent = !!selected && details?.layerId === selected.layerId && details.keyValue === selected.keyValue;
  // Sized and contrasted against the layer's own marker, and the statistic highlight when over it.
  const style = useMemo(() => {
    if (!details) return null;
    const look = resolveFeatureLook(layersById.get(details.layerId), details.attributes);
    return resolveHighlightStyle(HIGHLIGHT_HEX, look, isOverStatistic ? STATISTIC_UNDER_COLORS : NO_UNDER_COLORS);
  }, [details, layersById, isOverStatistic]);
  // Fly once per selection; a background refetch of its details must not pull the camera back after the user pans.
  const centeredKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selected) centeredKeyRef.current = null;
  }, [selected]);

  // Frames a newly picked statistic's layer, once per statistic; an asset picked meanwhile keeps the camera.
  const zoomedStatisticRef = useRef<number | null>(null);
  const hasSelection = !!selected;
  useEffect(() => {
    if (statisticId === null) {
      zoomedStatisticRef.current = null;
      return;
    }
    if (!engine || !isStatisticBoundsLoaded || zoomedStatisticRef.current === statisticId) return;
    zoomedStatisticRef.current = statisticId;
    if (hasSelection) return;
    // Same feedback as Zoom to layer when there is nothing to frame.
    if (!statisticExtent) Notification({ message: NO_FEATURES_TO_ZOOM_MESSAGE, type: NotificationTypes.INFO });
    else engine.flyTo(statisticExtent, { duration: ZOOM_TO_LAYER_SECONDS });
  }, [engine, statisticId, statisticExtent, isStatisticBoundsLoaded, hasSelection]);

  // A list pick carries its box, so the flight starts at once and the highlight lands when the details arrive.
  useEffect(() => {
    if (!engine || !selected?.bbox) return;
    const key = `${selected.layerId}|${selected.keyValue}`;
    if (centeredKeyRef.current === key) return;
    centeredKeyRef.current = key;
    flyToAsset(engine, { bbox: selected.bbox });
  }, [engine, selected]);

  useEffect(() => {
    if (!engine || !details || !isCurrent || !style) return;
    drawAssetHighlight(engine.layerPrimitives(HIGHLIGHT_KEY), details.geometry, style);
    // Layers created after the highlight would otherwise draw over it.
    engine.raiseLayerToTop(HIGHLIGHT_KEY);
    const key = `${details.layerId}|${details.keyValue}`;
    if (centeredKeyRef.current !== key) {
      centeredKeyRef.current = key;
      flyToAsset(engine, details);
    }
    // Ground primitives need terrain heights before they show.
    void Cesium.GroundPrimitive.initializeTerrainHeights().then(engine.requestRender);
    engine.requestRender();
    return engine.own(() => engine.releaseLayer(HIGHLIGHT_KEY));
  }, [engine, details, isCurrent, style]);
};
